/*--External-- */
import _ from "lodash";
import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";

/**--Internal-- */
import Loading from "UIComponents/Loading";
import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import { uploadFileV2, deleteAttachment } from "modules/Services";
import { updateQuestionFieldsInCache } from "Worksheet/modules/WorksheetUtils";
import { updateIsQuestionTheaterModeEnable } from "Worksheet/modules/WorksheetModules";
import {
  MODE_STUDENT_ATTEMPT,
  MODE_TEACHER_PREVIEW,
  DEFAULT_MEDIA_SUBMISSION_METHOD,
} from "Worksheet/modules/WorksheetConstants";

/**--Relative--*/
import classes from "./VideoResponseField.scss";
import {
  getPlaceholderAttachment,
  getVideoResponseFieldStyles,
} from "./VideoResponseFieldUtils";
import AddVideoResponseButton from "./AddVideoResponseButton";
import RecordVideoResponse from "./RecordVideoResponse";
import DisplayVideoResponse from "./DisplayVideoResponse";
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";

/**--Styles--*/
const styles = getVideoResponseFieldStyles();

/**
 *
 * @param {*} props
 * @returns Video Response Question Component
 */
const VideoResponseField = props => {
  const {
    t,
    mode,
    value,
    fieldId,
    parentId,
    questionId,
    resourceId,
    portalType,
    parentType,
    uploadFileV2,
    worksheetMode,
    isSubQuestion,
    questionIndex,
    updateInputField,
    deleteAttachment,
    submissionMethod,
    videoDurationLimit,
    subQuestionParentId,
    isFromInternalTool = false,
    makeAutoSubmitOnTimesUp = false,
    updateIsQuestionTheaterModeEnable = _.noop,
    isQueriesV2Enabled = false,
  } = props;
  const isPreview = mode === "preview";
  const [isUploading, setIsUploading] = useState(false);
  const [localAttachment, setLocalAttachment] = useState(null);
  const [openRecordingModal, setOpenRecordingModal] = useState(false);

  const attachment = isPreview ? localAttachment : value;

  // * Update attachment based on the mode
  const updateAttachments = useCallback(
    async attachments => {
      if (isPreview) {
        setLocalAttachment(_.first(attachments));
        setIsUploading(false);
        return;
      }

      updateQuestionFieldsInCache({
        fields: [
          {
            id: fieldId,
            resolveFields: { id: fieldId, attachments: attachments },
          },
        ],
        questionId,
        isSubQuestion,
        isFromInternalTool,
        subQuestionParentId,
        isQueriesV2Enabled,
      });
      updateInputField({});
      setIsUploading(false);
      updateIsQuestionTheaterModeEnable(false);
    },
    [updateInputField, fieldId, mode]
  );

  // * Delete attachment based on the mode
  const handleDeleteAttachment = useCallback(
    ({ attachmentId, nodeId }) => {
      updateAttachments([]);
      if (attachmentId) deleteAttachment({ attachmentId, nodeId });
      if (isPreview) setLocalAttachment(null);
    },
    [mode, deleteAttachment]
  );

  // * Full screen loader when times up and uploading media
  if (isUploading && makeAutoSubmitOnTimesUp) return <FullScreenLoader />;

  // * Loading media placeholder
  if (isUploading) {
    return (
      <div className={classes.containerWrapperPlaceholder}>
        <Loading
          position="center"
          isMiniLoader={true}
          containerStyle={styles.loaderStyle}
        />
        <MediaPlaceholder
          imageMaxWidth={556}
          imageMaxHeight={312}
          attachments={[getPlaceholderAttachment()]}
          containerStyle={styles.placeholderContainerStyle}
        />
      </div>
    );
  }

  // * Display video response
  if (!_.isEmpty(attachment)) {
    return (
      <DisplayVideoResponse
        t={t}
        mode={mode}
        questionId={questionId}
        attachment={attachment}
        worksheetMode={worksheetMode}
        deleteAttachment={handleDeleteAttachment}
      />
    );
  }

  // * Video response recorder
  if (openRecordingModal) {
    return (
      <RecordVideoResponse
        t={t}
        mode={mode}
        questionId={questionId}
        resourceId={resourceId}
        uploadFileV2={uploadFileV2}
        worksheetMode={worksheetMode}
        isSubQuestion={isSubQuestion}
        questionIndex={questionIndex}
        setIsUploading={setIsUploading}
        updateAttachments={updateAttachments}
        isFromInternalTool={isFromInternalTool}
        videoDurationLimit={videoDurationLimit}
        subQuestionParentId={subQuestionParentId}
        parentId={mode === "preview" ? null : parentId}
        closeModal={() => setOpenRecordingModal(false)}
        makeAutoSubmitOnTimesUp={makeAutoSubmitOnTimesUp}
        updateIsQuestionTheaterModeEnable={updateIsQuestionTheaterModeEnable}
        isQueriesV2Enabled={isQueriesV2Enabled}
      />
    );
  }
  const labelClasses = classNames("text-body-s", classes.label);

  const showLabel =
    _.isEmpty(attachment) &&
    _.includes([MODE_TEACHER_PREVIEW, MODE_STUDENT_ATTEMPT], worksheetMode);

  // * Button to start video recording
  return (
    <div>
      {showLabel && (
        <div className={labelClasses}>
          {t("worksheet:max_duration_in_min", {
            count: videoDurationLimit / 60000,
          })}
        </div>
      )}
      <AddVideoResponseButton
        t={t}
        fieldId={fieldId}
        isPreview={isPreview}
        parentType={parentType}
        portalType={portalType}
        submissionMethod={submissionMethod}
        updateAttachments={updateAttachments}
        updateLocalAttachment={value => setLocalAttachment(value)}
        updateIsUploading={value => {
          updateIsQuestionTheaterModeEnable(value);
          setIsUploading(value);
        }}
        startRecording={() => {
          updateIsQuestionTheaterModeEnable(true);
          setOpenRecordingModal(true);
        }}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  makeAutoSubmitOnTimesUp: _.get(state, "worksheet.enableAutoSubmit", false),
});

const mapActionCreators = {
  uploadFileV2,
  deleteAttachment,
  updateIsQuestionTheaterModeEnable,
};

export default connect(mapStateToProps, mapActionCreators)(VideoResponseField);

VideoResponseField.propTypes = {
  t: PropTypes.func,
  mode: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.object),
  fieldId: PropTypes.string.isRequired,
  fieldKey: PropTypes.string,
  parentId: PropTypes.string.isRequired,
  worksheetMode: PropTypes.string,
  parentType: PropTypes.string,
  questionId: PropTypes.string.isRequired,
  resourceId: PropTypes.string,
  isSubQuestion: PropTypes.bool,
  questionIndex: PropTypes.number,
  updateInputField: PropTypes.func.isRequired,
  uploadFileV2: PropTypes.func.isRequired,
  deleteAttachment: PropTypes.func.isRequired,
  videoDurationLimit: PropTypes.number,
  isFromInternalTool: PropTypes.bool,
  subQuestionParentId: PropTypes.string,
  submissionMethod: PropTypes.string,
  portalType: PropTypes.string,
  isQueriesV2Enabled: PropTypes.bool,
};

VideoResponseField.defaultProps = {
  t: () => {},
  mode: "",
  value: [],
  fieldKey: "",
  parentId: "",
  worksheetMode: "",
  parentType: "QUESTION_SUBMISSION_ATTACHMENT",
  questionId: "",
  resourceId: "",
  isSubQuestion: false,
  questionIndex: 0,
  updateInputField: _.noop,
  uploadFileV2: _.noop,
  deleteAttachment: _.noop,
  videoDurationLimit: 600000,
  isFromInternalTool: false,
  subQuestionParentId: "",
  submissionMethod: DEFAULT_MEDIA_SUBMISSION_METHOD,
  isQueriesV2Enabled: false,
};
